// Demo/test twin of the training RPCs (46_gavion_training.sql): same counts and rules, over a RecordStore (FakeRest
// in the demo build, memory in tests). training_progress rows are stored like the SQL table; field missions are
// computed from calls, dial_sessions, deals and sales.prefs on every read, as my_training_status() does.
import type { RecordStore } from "../../dialer/api/recordStore";
import { DIAL_OUTCOMES } from "../../domain/dialer/outcomes";
import { demoLinked } from "../../domain/dialer/contactCall"; // GAVION demo-contact
import {
  gameXp,
  isPlayableKey,
  type PlayableKey,
} from "../../domain/training/missions";
import {
  fieldMissionsFrom,
  type FieldStats,
  type ProgressRow,
  type TrainingStatus,
} from "../../domain/training/progress";
import { TrainingApiError, type TrainingApi } from "./types";

type Row = Record<string, unknown> & { id: number };
type ProgressRecord = ProgressRow & {
  id: number;
  sales_id: number;
  created_at?: string;
};

const DIAL: readonly string[] = DIAL_OUTCOMES.map((o) => o.code);
const HUMAN: readonly string[] = DIAL_OUTCOMES.filter(
  (o) => o.reachedHuman,
).map((o) => o.code);
const RESOURCE = "training_progress";

const toRow = (r: ProgressRecord): ProgressRow => ({
  mission_key: r.mission_key,
  completed_at: r.completed_at ?? null,
  best_score: r.best_score,
  attempts: r.attempts,
  xp: r.xp,
  updated_at: r.updated_at ?? null,
});

export function createFakeTrainingApi({
  store,
  me,
  isAdmin = false,
  now = () => new Date(),
}: {
  store: RecordStore;
  me: number;
  isAdmin?: boolean;
  now?: () => Date;
}): TrainingApi {
  const statusFor = async (sale: Row): Promise<TrainingStatus> => {
    const id = Number(sale.id);
    const [calls, sessions, deals, progress] = await Promise.all([
      store.list<Row>("calls"),
      store.list<Row>("dial_sessions"),
      store.list<Row>("deals"),
      store.list<ProgressRecord>(RESOURCE),
    ]);
    const mine = calls.filter(
      (c) =>
        Number(c.sales_id) === id && (c.direction ?? "outbound") === "outbound",
    );
    const outcome = (c: Row) => String(c.outcome ?? "");
    const prefs = (sale.prefs ?? {}) as Record<string, unknown>;
    const verified = prefs.softphone_verified_at;
    const stats: FieldStats = {
      softphone_verified:
        typeof verified === "string" && verified.trim() !== "",
      sessions: sessions.filter((s) => Number(s.sales_id) === id).length,
      logged_calls: mine.filter((c) => DIAL.includes(outcome(c))).length,
      reached_human: mine.filter((c) => HUMAN.includes(outcome(c))).length,
      callbacks: mine.filter(
        (c) => outcome(c) === "talked_callback" && c.callback_at != null,
      ).length,
      demos:
        mine.filter((c) => outcome(c) === "demo_booked").length +
        // twin of 62_gavion_deal_owner.sql §4: coalesce(setter_sales_id, sales_id)
        deals.filter(
          (d) =>
            Number(d.setter_sales_id ?? d.sales_id) === id && d.demo_at != null,
        ).length,
      // GAVION demo-contact (SQL twin: training_status_for 68_): a demo-linked main-line call is no contact call
      contact_calls: mine.filter(
        (c) => c.contact_id != null && !demoLinked({ demo: c.demo }),
      ).length,
      unlogged: mine.filter((c) => outcome(c) === "unlogged").length,
    };
    const rows = progress
      .filter((p) => Number(p.sales_id) === id)
      .sort((a, b) => a.mission_key.localeCompare(b.mission_key));
    const last = rows
      .map((r) => r.updated_at)
      .filter((x): x is string => !!x)
      .sort()
      .at(-1);
    return {
      sales_id: id,
      first_name: (sale.first_name as string | undefined) ?? null,
      last_name: (sale.last_name as string | undefined) ?? null,
      progress: rows.map(toRow),
      stats,
      missions: fieldMissionsFrom(stats),
      last_activity: last ?? null,
    };
  };

  const meRow = async (): Promise<Row> => {
    const sale = await store.get<Row>("sales", me);
    if (!sale || sale.disabled) throw new TrainingApiError("forbidden");
    return sale;
  };

  return {
    async myStatus() {
      return statusFor(await meRow());
    },
    async teamStatus() {
      if (!isAdmin) throw new TrainingApiError("forbidden");
      const sales = (await store.list<Row>("sales")).filter((s) => !s.disabled);
      sales.sort(
        (a, b) =>
          String(a.first_name ?? "").localeCompare(
            String(b.first_name ?? ""),
          ) ||
          String(a.last_name ?? "").localeCompare(String(b.last_name ?? "")) ||
          a.id - b.id,
      );
      return Promise.all(sales.map(statusFor));
    },
    async recordAttempt(key: PlayableKey, score: number) {
      await meRow();
      if (!isPlayableKey(key)) throw new TrainingApiError("invalid_mission");
      if (!Number.isInteger(score) || score < 0 || score > 100)
        throw new TrainingApiError("invalid_score");
      const stamp = now().toISOString();
      const existing = (await store.list<ProgressRecord>(RESOURCE)).find(
        (p) => Number(p.sales_id) === me && p.mission_key === key,
      );
      // trg_training_progress_guard twin: best kept, xp and completed_at recomputed
      const best = Math.max(existing?.best_score ?? 0, score);
      const xp = gameXp(key, best);
      const fields = {
        best_score: best,
        attempts: (existing?.attempts ?? 0) + 1,
        xp,
        completed_at: xp > 0 ? (existing?.completed_at ?? stamp) : null,
        updated_at: stamp,
      };
      const saved = existing
        ? await store.update<ProgressRecord>(RESOURCE, existing.id, fields)
        : await store.create<ProgressRecord>(RESOURCE, {
            sales_id: me,
            mission_key: key,
            created_at: stamp,
            ...fields,
          });
      return toRow(saved);
    },
  };
}
